"use client"

import * as React from "react"
import { useState, useEffect } from "react"
import { useAuth } from "@/context/AuthContext";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Toaster } from "@/components/ui/sonner"
import { Checkbox } from "@/components/ui/checkbox"

export default function Page() {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, signUp } = useAuth();

  const form = useForm({
    defaultValues: {
      inviteCode: "",
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      acceptTerms: false,
    },
  });

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      router.push("/");
    }
  }, [user, router]);

  // Handle Toast notifications for errors
  useEffect(() => {
    if (error) {
      toast("Problem!", {
        description: error,
        duration: Infinity,
        action: {
          label: "Dismiss",
          onClick: () => {
            toast.dismiss()
            setError(null)
          }
        },
      })
    }
  }, [error]);

  const onSubmit = async (values: any) => {

    if (!values.acceptTerms) {
      setError("You must accept the terms and conditions to continue.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      if (values.password !== values.confirmPassword) {
        throw new Error("Passwords do not match");
      }

      await signUp({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        inviteCode: values.inviteCode,
        confirmPassword: values.confirmPassword,
        plan: searchParams.get("plan") ?? undefined,
        interval: searchParams.get("interval") ?? undefined,
      });
      // signUp() itself redirects to /verify — email confirmation is
      // still pending, so there's nothing further to navigate to here.
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="w-full flex flex-col justify-center items-center mt-32 mb-32">
      <Card className="w-sm">
        <CardHeader>
          <CardTitle>Sign up</CardTitle>
          <CardDescription>
            The beta version of LawLibrary.AI is currently invite-only. If you have an invite code, please sign up below to get started.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid gap-4">

                <FormField
                  control={form.control}
                  name="inviteCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invite code</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First name</FormLabel>
                        <FormControl>
                          <Input type="text" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last name</FormLabel>
                        <FormControl>
                          <Input type="text" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormDescription>
                        Your password must be at least 8 characters long and include at least one capital letter, lower case letter, number, and special character.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="acceptTerms"
                  render={({ field }) => (
                    <FormItem className="flex flex-row justify-start items-center space-x-2 h-10 mt-2 rounded-md border px-4 py-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          required
                        />
                      </FormControl>
                      <div>
                        <FormLabel className="gap-0">
                          <span className="font-normal text-sm text-foreground">
                            I agree to the{" "}
                            <Link href="/terms" className="text-primary underline">
                              Terms
                            </Link> {" "}
                            and {" "}
                            <Link href="/privacy" className="text-primary underline">
                              Privacy Policy
                            </Link>.
                          </span>
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full mt-2" disabled={loading} size="lg">
                  {loading ? "Creating account..." : "Sign up"}
                </Button>
              </div>
            </form>
            <div className="mt-4 text-center text-sm font-medium">
              Already have an account?{" "}
              <Link href="/sign-in" className="underline">
                Sign in.
              </Link>
            </div>
          </Form>
        </CardContent>
      </Card>
      <Toaster />
    </div>
  )
}